import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { useAuth } from 'shared/hooks/useAuth';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import {
  assetRegisterApi,
  listStatusParams,
  riskRegisterApi,
  softwareApi,
} from '../api/systemRegisterApi';
import {
  INCIDENT_SEVERITY,
  RESOLUTION_STATUS,
  formatEnumLabel,
} from '../constants/enums';
import { apiErrorMessage, unwrapList, unwrapRecords } from '../utils/responseHelpers';
import FormSection from './FormSection';

/** Local calendar date as `yyyy-MM-dd` for date inputs. */
export const todayIsoDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const isBasicUserRole = (user) => {
  const role = user?.data?.role;
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'USER';
};

const normalizePersonKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/** True when `stored` looks like the same person as name/email (or "Name (email)"). */
const matchesIncidentPerson = (stored, name, email) => {
  const raw = normalizePersonKey(stored);
  if (!raw) return false;
  const n = normalizePersonKey(name);
  const e = normalizePersonKey(email);
  if (e && (raw === e || raw.includes(e))) return true;
  if (n && (raw === n || raw.startsWith(`${n} (`))) return true;
  if (n && e) {
    const label = normalizePersonKey(`${name} (${email})`);
    if (raw === label) return true;
  }
  return false;
};

const userFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const softwareFilter = createFilterOptions({
  stringify: (option) =>
    `${option.label ?? ''} ${option.softwareName ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const assetFilter = createFilterOptions({
  stringify: (option) =>
    `${option.assetId ?? ''} ${option.assetName ?? ''} ${option.assetTag ?? ''} ${option.serialNumber ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const softwareLabel = (s) => {
  if (!s) return '';
  return (s.label || s.softwareName || '').trim() || `Software #${s.id}`;
};

const assetLabel = (a) => {
  if (!a) return '';
  const id = (a.assetId || '').trim();
  const name = (a.assetName || a.label || '').trim() || `Asset #${a.id}`;
  return id ? `${id} — ${name}` : name;
};

const riskRegisterFilter = createFilterOptions({
  stringify: (option) =>
    `${option.riskName ?? ''} ${option.riskCategory ?? ''} ${option.riskPriority ?? ''} ${option.riskOwner ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const userLabel = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

const riskRegisterLabel = (r) => {
  if (!r) return '';
  const name = (r.riskName || '').trim() || `Risk #${r.id}`;
  const priority = r.riskPriority ? formatEnumLabel(r.riskPriority) : '';
  const status = r.status ? formatEnumLabel(r.status) : '';
  const meta = [priority, status].filter(Boolean).join(' · ');
  return meta ? `${name} (${meta})` : name;
};

/**
 * Resolve a saved string (email / name / "Name (email)") to a tenant user option.
 */
const findUserOption = (users, value) => {
  const raw = (value || '').trim();
  if (!raw) return null;
  const byEmail = users.find((u) => (u.email || '').trim().toLowerCase() === raw.toLowerCase());
  if (byEmail) return byEmail;
  const byName = users.find((u) => (u.name || '').trim().toLowerCase() === raw.toLowerCase());
  if (byName) return byName;
  const byLabel = users.find((u) => userLabel(u).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel;
  // Preserve legacy free-text values not in the current tenant list.
  return { id: `legacy-${raw}`, name: raw, email: '' };
};

/**
 * @param {{ incidentReportingStaff?: string, reportedBy?: string, incidentDate?: string }} [defaults]
 */
export const emptyIncidentRow = (defaults = {}) => {
  const incidentReportingStaff =
    typeof defaults === 'string' ? '' : defaults.incidentReportingStaff || '';
  const reportedBy =
    typeof defaults === 'string' ? '' : defaults.reportedBy || incidentReportingStaff || '';
  const incidentDate =
    typeof defaults === 'string' ? todayIsoDate() : defaults.incidentDate || todayIsoDate();

  return {
    key: `inc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: null,
    incidentTicket: '',
    incidentDate,
    incidentDetails: '',
    actionTaken: '',
    rootCause: '',
    reportedBy: reportedBy || '',
    incidentReportingStaff: incidentReportingStaff || '',
    riskRegisterIds: [],
    risks: [],
    riskPriority: '',
    severityLevel: '',
    resolutionStatus: 'OPEN',
    incidentResponseOwner: '',
    knownIncidents: true,
    escalationPathDefined: false,
    softwareId: null,
    targetType: 'SOFTWARE',
    assetRegisterIds: [],
    assets: [],
    approvals: [],
  };
};

const normalizeRiskIds = (values) => {
  if (Array.isArray(values?.riskRegisterIds) && values.riskRegisterIds.length) {
    return values.riskRegisterIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
  }
  if (values?.riskRegisterId != null) {
    return [Number(values.riskRegisterId)].filter((id) => !Number.isNaN(id));
  }
  return [];
};

const highestPriority = (options) => {
  const rank = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
  let best = '';
  let bestRank = 0;
  for (const opt of options || []) {
    const p = opt?.riskPriority || '';
    const r = rank[p] || 0;
    if (r > bestRank) {
      bestRank = r;
      best = p;
    }
  }
  return best;
};

/**
 * Create / edit dialog for an incident.
 * When `selectTargetType` is true (Add Incident), user chooses IT Asset or Software.
 * IT Asset: multi-select of devices assigned to the current user (ITAssetIncidents M2M).
 * Software: select from Software Register.
 * When `selectSoftware` alone is true, only software selection is shown (legacy).
 */
export default function IncidentDialog({
  open,
  mode = 'create',
  initialValues,
  defaultReportingStaff = '',
  saving = false,
  onClose,
  onSave,
  selectSoftware = false,
  selectTargetType = false,
  softwareOptions: softwareOptionsProp = null,
}) {
  const { user } = useAuth();
  const isBasicUser = isBasicUserRole(user);
  const showAdvancedFields = !isBasicUser;
  const readOnly = mode === 'view';
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const currentName = user?.data?.name?.trim() || '';
  const currentEmail = user?.data?.email?.trim() || '';
  const isSubmitter = useMemo(() => {
    if (isCreate) return true;
    return (
      matchesIncidentPerson(initialValues?.reportedBy, currentName, currentEmail)
      || matchesIncidentPerson(initialValues?.incidentReportingStaff, currentName, currentEmail)
    );
  }, [
    isCreate,
    initialValues?.reportedBy,
    initialValues?.incidentReportingStaff,
    currentName,
    currentEmail,
  ]);
  /** Original report (target + details) — only the submitter may change these on edit. */
  const canEditOriginal = !readOnly && (isCreate || isSubmitter);
  /** Resolution / handling fields — for responders (non-basic) on create/edit. */
  const canEditHandling = !readOnly && showAdvancedFields;
  const lockOriginal = isEdit && !isSubmitter;
  const showTargetPicker = Boolean(selectTargetType);
  const showSoftwareOnly = Boolean(selectSoftware) && !showTargetPicker;
  const [values, setValues] = useState(() =>
    emptyIncidentRow({ incidentReportingStaff: defaultReportingStaff })
  );
  const [errors, setErrors] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [riskOptions, setRiskOptions] = useState([]);
  const [loadingRisks, setLoadingRisks] = useState(false);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [loadingSoftware, setLoadingSoftware] = useState(false);
  const [assetOptions, setAssetOptions] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  const loadTenantUsers = useCallback(async () => {
    const registryTenantId = getRegistryTenantIdFromSession();
    if (!registryTenantId) {
      setTenantUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
      setTenantUsers(rows);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || 'Failed to load tenant users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadRiskRegisters = useCallback(async () => {
    setLoadingRisks(true);
    try {
      const response = await riskRegisterApi.list(listStatusParams(false));
      setRiskOptions(unwrapList(response));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load risk register.'));
      setRiskOptions([]);
    } finally {
      setLoadingRisks(false);
    }
  }, []);

  const loadSoftwareOptions = useCallback(async () => {
    if (!showSoftwareOnly && !showTargetPicker) {
      setSoftwareOptions([]);
      return;
    }
    if (Array.isArray(softwareOptionsProp) && softwareOptionsProp.length) {
      setSoftwareOptions(softwareOptionsProp);
      return;
    }
    setLoadingSoftware(true);
    try {
      const response = await softwareApi.list({ page: 0, size: 500, status: 'ACTIVE' });
      const { rows } = unwrapRecords(response);
      setSoftwareOptions(
        (rows || []).map((s) => ({
          id: s.id,
          label: s.softwareName || `Software #${s.id}`,
          softwareName: s.softwareName,
        }))
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load software register.'));
      setSoftwareOptions([]);
    } finally {
      setLoadingSoftware(false);
    }
  }, [showSoftwareOnly, showTargetPicker, softwareOptionsProp]);

  const loadAssignedAssets = useCallback(async () => {
    if (!showTargetPicker) {
      setAssetOptions([]);
      return;
    }
    setLoadingAssets(true);
    try {
      const response = await assetRegisterApi.mine();
      setAssetOptions(unwrapList(response));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load your assigned devices.'));
      setAssetOptions([]);
    } finally {
      setLoadingAssets(false);
    }
  }, [showTargetPicker]);

  useEffect(() => {
    if (!open) return;
    if (showAdvancedFields) {
      loadTenantUsers();
      loadRiskRegisters();
    }
    loadSoftwareOptions();
    loadAssignedAssets();
    const base = emptyIncidentRow({ incidentReportingStaff: defaultReportingStaff });
    const riskRegisterIds = showAdvancedFields ? normalizeRiskIds(initialValues) : [];
    const reporter = isCreate
      ? (defaultReportingStaff || '')
      : (initialValues?.reportedBy || initialValues?.incidentReportingStaff || '');
    const initialAssetIds = Array.isArray(initialValues?.assetRegisterIds)
      ? initialValues.assetRegisterIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    const inferredTarget =
      initialValues?.targetType
      || (initialAssetIds.length && !initialValues?.softwareId ? 'IT_ASSET' : 'SOFTWARE');
    setValues({
      ...base,
      ...(initialValues || {}),
      incidentDate: isCreate
        ? todayIsoDate()
        : initialValues?.incidentDate || todayIsoDate(),
      reportedBy: reporter,
      incidentReportingStaff: isCreate
        ? defaultReportingStaff || ''
        : (initialValues?.incidentReportingStaff
          || initialValues?.reportedBy
          || ''),
      incidentResponseOwner: showAdvancedFields
        ? (isCreate ? '' : initialValues?.incidentResponseOwner || '')
        : (initialValues?.incidentResponseOwner || reporter || ''),
      resolutionStatus: isCreate
        ? 'OPEN'
        : initialValues?.resolutionStatus || 'OPEN',
      riskRegisterIds,
      risks: showAdvancedFields && Array.isArray(initialValues?.risks) ? initialValues.risks : [],
      riskPriority: showAdvancedFields
        ? (initialValues?.riskPriority ?? initialValues?.severityLevel ?? '')
        : '',
      severityLevel: showAdvancedFields
        ? (initialValues?.severityLevel ?? initialValues?.riskPriority ?? '')
        : '',
      rootCause: showAdvancedFields ? (initialValues?.rootCause ?? '') : '',
      actionTaken: showAdvancedFields ? (initialValues?.actionTaken ?? '') : '',
      knownIncidents: showAdvancedFields ? (initialValues?.knownIncidents ?? true) : true,
      escalationPathDefined: showAdvancedFields
        ? Boolean(initialValues?.escalationPathDefined)
        : false,
      softwareId: initialValues?.softwareId ?? null,
      targetType: inferredTarget,
      assetRegisterIds: initialAssetIds,
      assets: Array.isArray(initialValues?.assets) ? initialValues.assets : [],
    });
    setErrors({});
  }, [
    open,
    initialValues,
    defaultReportingStaff,
    isCreate,
    showAdvancedFields,
    loadTenantUsers,
    loadRiskRegisters,
    loadSoftwareOptions,
    loadAssignedAssets,
  ]);

  useEffect(() => {
    if ((!showSoftwareOnly && !showTargetPicker) || !Array.isArray(softwareOptionsProp)) return;
    setSoftwareOptions(softwareOptionsProp);
  }, [showSoftwareOnly, showTargetPicker, softwareOptionsProp]);

  const ownerOption = useMemo(
    () => findUserOption(tenantUsers, values.incidentResponseOwner),
    [tenantUsers, values.incidentResponseOwner]
  );

  const selectedSoftware = useMemo(() => {
    if (values.softwareId == null) return null;
    return (
      softwareOptions.find((s) => String(s.id) === String(values.softwareId))
      || { id: values.softwareId, label: `Software #${values.softwareId}` }
    );
  }, [softwareOptions, values.softwareId]);

  const selectedAssets = useMemo(() => {
    const ids = new Set((values.assetRegisterIds || []).map((id) => String(id)));
    const fromOptions = assetOptions.filter((a) => ids.has(String(a.id)));
    const foundIds = new Set(fromOptions.map((a) => String(a.id)));
    const leftovers = (values.assets || [])
      .filter((a) => {
        const id = a?.assetRegisterId ?? a?.id;
        return id != null && ids.has(String(id)) && !foundIds.has(String(id));
      })
      .map((a) => ({
        id: a.assetRegisterId ?? a.id,
        assetId: a.assetId,
        assetName: a.assetName,
        assetTag: a.assetTag,
      }));
    return [...fromOptions, ...leftovers];
  }, [assetOptions, values.assetRegisterIds, values.assets]);

  const selectedRisks = useMemo(() => {
    const ids = new Set((values.riskRegisterIds || []).map((id) => String(id)));
    const fromOptions = riskOptions.filter((r) => ids.has(String(r.id)));
    const foundIds = new Set(fromOptions.map((r) => String(r.id)));
    const leftovers = (values.risks || [])
      .filter((r) => r?.riskRegisterId != null && ids.has(String(r.riskRegisterId)) && !foundIds.has(String(r.riskRegisterId)))
      .map((r) => ({
        id: r.riskRegisterId,
        riskName: r.riskName,
        riskPriority: r.riskPriority,
        status: r.status || '',
      }));
    return [...fromOptions, ...leftovers];
  }, [riskOptions, values.riskRegisterIds, values.risks]);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const applySelectedRisks = (options) => {
    const list = Array.isArray(options) ? options : [];
    const priority = highestPriority(list);
    setValues((prev) => ({
      ...prev,
      riskRegisterIds: list.map((o) => o.id).filter((id) => id != null),
      risks: list.map((o) => ({
        riskRegisterId: o.id,
        riskName: (o.riskName || '').trim(),
        riskPriority: o.riskPriority || '',
        status: o.status || '',
      })),
      riskPriority: priority,
      severityLevel: priority || prev.severityLevel,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.riskRegisterIds;
      return next;
    });
  };

  const applySelectedAssets = (options) => {
    const list = Array.isArray(options) ? options : [];
    setValues((prev) => ({
      ...prev,
      assetRegisterIds: list.map((o) => o.id).filter((id) => id != null),
      assets: list.map((o) => ({
        assetRegisterId: o.id,
        assetId: o.assetId,
        assetName: o.assetName,
        assetTag: o.assetTag,
      })),
    }));
    setErrors((prev) => {
      if (!prev.assetRegisterIds) return prev;
      const next = { ...prev };
      delete next.assetRegisterIds;
      return next;
    });
  };

  const setTargetType = (targetType) => {
    setValues((prev) => ({
      ...prev,
      targetType,
      softwareId: targetType === 'SOFTWARE' ? prev.softwareId : null,
      assetRegisterIds: targetType === 'IT_ASSET' ? prev.assetRegisterIds : [],
      assets: targetType === 'IT_ASSET' ? prev.assets : [],
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.softwareId;
      delete next.assetRegisterIds;
      delete next.targetType;
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (canEditOriginal) {
      if (showTargetPicker) {
        if (values.targetType === 'SOFTWARE' && values.softwareId == null) {
          next.softwareId = 'Select a software system';
        }
        if (values.targetType === 'IT_ASSET' && !(values.assetRegisterIds || []).length) {
          next.assetRegisterIds = 'Select at least one device';
        }
      } else if (showSoftwareOnly && values.softwareId == null) {
        next.softwareId = 'Select a software system';
      }
      if (!(values.incidentDetails || '').trim()) next.incidentDetails = 'Required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (readOnly) {
      onClose?.();
      return;
    }
    if (!validate() || saving) return;
    const staff = lockOriginal
      ? (initialValues?.incidentReportingStaff?.trim()
        || initialValues?.reportedBy?.trim()
        || values.incidentReportingStaff?.trim()
        || defaultReportingStaff
        || '')
      : (values.incidentReportingStaff?.trim() || defaultReportingStaff || '');
    const priority = canEditHandling
      ? (values.riskPriority || highestPriority(selectedRisks) || '')
      : (initialValues?.riskPriority || initialValues?.severityLevel || '');
    const owner = canEditHandling
      ? (values.incidentResponseOwner?.trim() || '')
      : (initialValues?.incidentResponseOwner?.trim() || values.incidentResponseOwner?.trim() || staff);

    const lockedTargetType = initialValues?.targetType
      || (initialValues?.softwareId != null
        ? 'SOFTWARE'
        : (initialValues?.assetRegisterIds || []).length
          ? 'IT_ASSET'
          : 'SOFTWARE');
    const targetType = lockOriginal
      ? lockedTargetType
      : (showTargetPicker ? (values.targetType || 'SOFTWARE') : 'SOFTWARE');
    const isAssetTarget = targetType === 'IT_ASSET';

    await onSave?.({
      ...values,
      // Preserve original report when the current user is not the submitter.
      targetType,
      softwareId: lockOriginal
        ? (initialValues?.softwareId ?? null)
        : (isAssetTarget ? null : (values.softwareId ?? null)),
      assetRegisterIds: lockOriginal
        ? (initialValues?.assetRegisterIds || [])
        : (isAssetTarget ? (values.assetRegisterIds || []) : []),
      assets: lockOriginal
        ? (initialValues?.assets || [])
        : (isAssetTarget ? (values.assets || []) : []),
      incidentDate: lockOriginal
        ? (initialValues?.incidentDate || values.incidentDate || todayIsoDate())
        : (values.incidentDate || todayIsoDate()),
      incidentDetails: lockOriginal
        ? (initialValues?.incidentDetails || '').trim()
        : values.incidentDetails.trim(),
      actionTaken: canEditHandling
        ? (values.actionTaken?.trim() || '')
        : (initialValues?.actionTaken?.trim() || ''),
      rootCause: canEditHandling
        ? (values.rootCause?.trim() || '')
        : (initialValues?.rootCause?.trim() || ''),
      reportedBy: lockOriginal
        ? (initialValues?.reportedBy?.trim() || staff)
        : (values.reportedBy?.trim() || staff),
      incidentReportingStaff: staff,
      riskRegisterIds: canEditHandling
        ? (values.riskRegisterIds || [])
        : (initialValues?.riskRegisterIds || []),
      risks: canEditHandling ? (values.risks || []) : (initialValues?.risks || []),
      riskPriority: priority,
      severityLevel: canEditHandling
        ? (values.severityLevel || priority || '')
        : (initialValues?.severityLevel || priority || ''),
      resolutionStatus: canEditHandling
        ? (values.resolutionStatus || 'OPEN')
        : (initialValues?.resolutionStatus || values.resolutionStatus || 'OPEN'),
      incidentResponseOwner: owner,
      knownIncidents: canEditHandling
        ? Boolean(values.knownIncidents)
        : Boolean(initialValues?.knownIncidents ?? true),
      escalationPathDefined: canEditHandling
        ? Boolean(values.escalationPathDefined)
        : Boolean(initialValues?.escalationPathDefined),
    });
  };

  const title = isEdit
    ? (lockOriginal ? 'Update incident resolution' : 'Edit incident')
    : 'Add incident';
  const fieldSx = { '& .MuiFormHelperText-root': { mx: 0 } };
  const selectSx = {
    ...fieldSx,
    minWidth: '100%',
    width: '100%',
    minHeight: 48,
    '& .MuiInputLabel-root': { whiteSpace: 'nowrap' },
    '& .MuiSelect-select': {
      minHeight: '1.5em',
      display: 'flex',
      alignItems: 'center',
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.paper', py: 2 }}>
        {showTargetPicker ? (
          <FormSection title="Incident target">
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <FormControl component="fieldset" disabled={!canEditOriginal}>
                  <FormLabel component="legend" sx={{ mb: 0.5, fontSize: 13 }}>
                    Submit incident for
                  </FormLabel>
                  <RadioGroup
                    row
                    value={values.targetType || 'SOFTWARE'}
                    onChange={(e) => setTargetType(e.target.value)}
                  >
                    <FormControlLabel value="IT_ASSET" control={<Radio size="small" />} label="IT Asset" />
                    <FormControlLabel value="SOFTWARE" control={<Radio size="small" />} label="Software" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {values.targetType === 'IT_ASSET' ? (
                <Grid size={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={assetOptions}
                    loading={loadingAssets}
                    disabled={!canEditOriginal}
                    readOnly={lockOriginal}
                    filterOptions={assetFilter}
                    value={selectedAssets}
                    onChange={(_, options) => applySelectedAssets(options)}
                    getOptionLabel={(option) => assetLabel(option)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    noOptionsText={
                      loadingAssets
                        ? 'Loading…'
                        : 'No devices assigned to you'
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Device(s)"
                        placeholder="Select assigned device(s)…"
                        error={Boolean(errors.assetRegisterIds)}
                        helperText={
                          errors.assetRegisterIds
                          || (!lockOriginal ? 'Only IT assets assigned to you are listed' : undefined)
                        }
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          readOnly: lockOriginal,
                          endAdornment: (
                            <>
                              {loadingAssets ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              ) : (
                <Grid size={12}>
                  <Autocomplete
                    size="small"
                    options={softwareOptions}
                    loading={loadingSoftware}
                    disabled={!canEditOriginal}
                    readOnly={lockOriginal}
                    filterOptions={softwareFilter}
                    value={selectedSoftware}
                    onChange={(_, option) => setField('softwareId', option?.id ?? null)}
                    getOptionLabel={(option) => softwareLabel(option)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Software"
                        error={Boolean(errors.softwareId)}
                        helperText={errors.softwareId}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          readOnly: lockOriginal,
                          endAdornment: (
                            <>
                              {loadingSoftware ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </FormSection>
        ) : null}
        {showSoftwareOnly ? (
          <FormSection title="Software">
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <Autocomplete
                  size="small"
                  options={softwareOptions}
                  loading={loadingSoftware}
                  disabled={!canEditOriginal}
                  readOnly={lockOriginal}
                  filterOptions={softwareFilter}
                  value={selectedSoftware}
                  onChange={(_, option) => setField('softwareId', option?.id ?? null)}
                  getOptionLabel={(option) => softwareLabel(option)}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Software"
                      error={Boolean(errors.softwareId)}
                      helperText={errors.softwareId}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        ...params.InputProps,
                        readOnly: lockOriginal,
                        endAdornment: (
                          <>
                            {loadingSoftware ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>
        ) : null}

        <FormSection title={lockOriginal ? 'Original report' : '1. Incident details'}>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {(lockOriginal || !isCreate) && (values.reportedBy || values.incidentReportingStaff) ? (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Reported by:{' '}
                  <Typography component="span" variant="body2" color="text.primary" fontWeight={600}>
                    {values.reportedBy || values.incidentReportingStaff}
                  </Typography>
                </Typography>
              </Grid>
            ) : null}
            <Grid size={12}>
              <TextField
                label="Incident details"
                fullWidth
                size="small"
                required={canEditOriginal}
                multiline
                minRows={3}
                disabled={!canEditOriginal}
                InputProps={{ readOnly: lockOriginal }}
                value={values.incidentDetails || ''}
                onChange={(e) => setField('incidentDetails', e.target.value)}
                error={Boolean(errors.incidentDetails)}
                helperText={errors.incidentDetails}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </FormSection>

        {showAdvancedFields ? (
          <>
            <FormSection
              title={lockOriginal ? 'Your resolution' : '2. Resolution & response'}
            >
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={12}>
                  <TextField
                    label="Root cause"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    disabled={!canEditHandling}
                    value={values.rootCause || ''}
                    onChange={(e) => setField('rootCause', e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Action taken"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    disabled={!canEditHandling}
                    value={values.actionTaken || ''}
                    onChange={(e) => setField('actionTaken', e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title={lockOriginal ? 'Classification & ownership' : '3. Classification & ownership'}>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SearchableSelect
                    label="Severity level"
                    size="small"
                    disabled={!canEditHandling}
                    value={values.severityLevel || ''}
                    onChange={(next) => setField('severityLevel', next)}
                    sx={selectSx}
                    options={INCIDENT_SEVERITY.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SearchableSelect
                    label="Resolution status"
                    size="small"
                    allowEmpty={false}
                    disabled={!canEditHandling || isCreate}
                    value={values.resolutionStatus || (isCreate ? 'OPEN' : '')}
                    onChange={(next) => setField('resolutionStatus', next)}
                    sx={selectSx}
                    options={RESOLUTION_STATUS.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    size="small"
                    options={tenantUsers}
                    loading={loadingUsers}
                    disabled={!canEditHandling}
                    filterOptions={userFilter}
                    value={ownerOption}
                    onChange={(_, option) =>
                      setField('incidentResponseOwner', option?.name || option?.email || '')
                    }
                    getOptionLabel={(option) => userLabel(option)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Incident response owner"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(values.knownIncidents)}
                        onChange={(e) => setField('knownIncidents', e.target.checked)}
                        disabled={!canEditHandling}
                      />
                    )}
                    label="Known incident"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection
              title={lockOriginal ? 'Associated risks' : '4. Associated risks'}
            >
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={riskOptions}
                    loading={loadingRisks}
                    disabled={!canEditHandling}
                    filterOptions={riskRegisterFilter}
                    value={selectedRisks}
                    onChange={(_, options) => applySelectedRisks(options)}
                    getOptionLabel={(option) => riskRegisterLabel(option)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Associated risks"
                        placeholder="Select risks…"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingRisks ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" variant="outlined" disabled={saving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly ? (
          <Button onClick={handleSave} variant="contained" color="secondary" disabled={saving}>
            {saving ? 'Saving…' : (lockOriginal ? 'Save resolution' : 'Save')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
